<template>
    <div class="sf-audio-question">
        <div v-if="fieldLabel" class="aq-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="aq-required">*</span>
        </div>

        <div class="aq-player-wrap">
            <audio
                v-if="audioSrc"
                :src="audioSrc"
                class="aq-audio"
                controls
                preload="metadata"
            ></audio>
            <div v-else class="aq-empty">Ses dosyası yüklenemedi</div>
        </div>

        <div v-if="config.transcript" class="aq-transcript">
            <div class="aq-transcript-title">Transkript</div>
            <div class="aq-transcript-text">{{ config.transcript }}</div>
        </div>

        <div v-if="config.answerType && config.answerType !== 'none'" class="aq-answer">
            <input
                v-if="config.answerType === 'shortText'"
                type="text"
                class="aq-input"
                :value="(internalValue && internalValue.answer) || ''"
                :placeholder="answerPlaceholder"
                @input="emitAnswer($event.target.value)"
            />
            <textarea
                v-else-if="config.answerType === 'longText'"
                class="aq-textarea"
                :value="(internalValue && internalValue.answer) || ''"
                :placeholder="answerPlaceholder"
                rows="3"
                @input="emitAnswer($event.target.value)"
            />
            <div v-else-if="config.answerType === 'singleChoice'" class="aq-choice-list">
                <label
                    v-for="(opt, idx) in answerOptions"
                    :key="idx"
                    class="aq-choice-item"
                    :class="{ active: (internalValue && internalValue.answer) === opt }"
                >
                    <input
                        type="radio"
                        :checked="(internalValue && internalValue.answer) === opt"
                        :name="'aq-' + fieldId"
                        @change="emitAnswer(opt)"
                    />
                    <span>{{ opt }}</span>
                </label>
            </div>
        </div>

        <div v-if="showError && hasError" class="aq-error">
            Bu alan zorunludur
        </div>
    </div>
</template>

<script>
export default {
    name: "AudioQuestion",
    props: {
        value: { type: [Object, String, Number, Array], default: null },
        audioQuestion: { type: Object, default: () => ({}) },
        label: { type: String, default: "" },
        required: { type: Boolean, default: false },
        showError: { type: Boolean, default: false },
        formId: { type: String, default: "" },
        fieldId: { type: String, default: "" },
        order: { type: Number, default: 0 },
    },
    data() {
        return {
            internalValue: this.value || { answer: null },
        };
    },
    computed: {
        config() { return this.audioQuestion || {}; },
        fieldLabel() { return this.label || ""; },
        isRequired() { return !!this.required; },
        audioSrc() {
            return this.resolveAudioUrl(this.config.externalUrl, this.config.asset);
        },
        answerOptions() {
            if (!this.config.answerConfig) return [];
            return this.config.answerConfig.items || this.config.answerConfig.options || [];
        },
        answerPlaceholder() {
            return (this.config.answerConfig && this.config.answerConfig.placeholder) || "Cevabınız...";
        },
        hasError() {
            if (!this.config.answerType || this.config.answerType === "none") return false;
            if (!this.isRequired) return false;
            const ans = this.internalValue && this.internalValue.answer;
            return ans === null || ans === undefined || ans === "";
        },
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && typeof newVal === "object") {
                    this.internalValue = Object.assign({}, this.internalValue, newVal);
                }
            },
            immediate: true,
        },
    },
    methods: {
        resolveAudioUrl(externalUrl, asset) {
            if (externalUrl) return externalUrl;
            if (!asset) return null;
            if (typeof asset === "string") return asset;
            if (asset.cdnUrl) return asset.cdnUrl;
            if (asset.url) return asset.url;
            if (asset.path && asset._id && asset.ext) {
                return "https://static.supsis.live/" + asset.path + asset._id + "." + asset.ext;
            }
            return null;
        },
        emitAnswer(answer) {
            this.$set(this.internalValue, "answer", answer);
            this.$emit("input", this.internalValue);
            this.$emit("validate", { fieldId: this.fieldId, hasError: this.hasError });
        },
    },
};
</script>

<style lang="scss" scoped>
.sf-audio-question {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

.aq-label {
    font-weight: 600;
    color: var(--text-primary, #1f2937);
    font-size: clamp(14px, 1.8vw, 16px);
    .aq-required { color: #dc2626; margin-left: 4px; }
}

.aq-player-wrap {
    background: linear-gradient(135deg, #f3e8ff 0%, #ecfeff 100%);
    border-radius: 10px;
    padding: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 70px;

    .aq-audio { width: 100%; }
    .aq-empty { color: #9ca3af; font-size: 13px; }
}

.aq-transcript {
    background: #f9fafb;
    border-left: 3px solid #ec4899;
    padding: 8px 12px;
    border-radius: 0 6px 6px 0;

    .aq-transcript-title {
        font-size: 11px;
        font-weight: 600;
        color: #831843;
        text-transform: uppercase;
        margin-bottom: 4px;
    }
    .aq-transcript-text {
        font-size: 13px;
        color: #4b5563;
        line-height: 1.5;
    }
}

.aq-answer { margin-top: 4px; }

.aq-input,
.aq-textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border, #d1d5db);
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    background: #ffffff;
    &:focus { outline: none; border-color: var(--primary, #ec4899); }
}

.aq-choice-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.aq-choice-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid var(--border, #e5e7eb);
    border-radius: 8px;
    cursor: pointer;
    background: #ffffff;

    &:hover { border-color: var(--primary, #ec4899); }
    &.active { border-color: var(--primary, #ec4899); background: #fdf2f8; }
}

.aq-error {
    color: #dc2626;
    font-size: 12px;
    margin-top: 4px;
}
</style>
